import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel, CircularProgress, Tooltip } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { adminService } from '../../services/api';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]); // flat
  const [categoriesTree, setCategoriesTree] = useState([]); // hierarchical
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name:'', description:'', parentId:'', isActive:true, imageUrl:'' });

  useEffect(()=>{ loadCategories(); },[]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const [flatRes, treeRes] = await Promise.all([
        adminService.getCategories(),
        adminService.getCategoriesTree()
      ]);
      setCategories(Array.isArray(flatRes.data)? flatRes.data: []);
      setCategoriesTree(Array.isArray(treeRes.data)? treeRes.data: []);
    } catch(e){
      console.error('Load categories failed', e);
      toast.error('Failed to load categories');
      setCategories([]); setCategoriesTree([]);
    } finally { setLoading(false); }
  };

  const flattenCategoriesForSelection = (tree, currentId=null, level=0) => {
    const out=[]; tree.forEach(c=>{ if(c.id!==currentId){ out.push({...c, displayName: ' '.repeat(level*2)+c.name, level}); if(c.children?.length){ out.push(...flattenCategoriesForSelection(c.children, currentId, level+1)); } } }); return out;
  };

  const renderCategoryTree = (tree, level=0) => tree.map(cat => (
    <React.Fragment key={cat.id}>
      <TableRow>
        <TableCell>
          {cat.imageUrl ? (
            <Box sx={{ width:48,height:36,borderRadius:1,overflow:'hidden',border:'1px solid #eee' }}>
              <img src={cat.imageUrl} alt={cat.name} style={{ width:'100%',height:'100%',objectFit:'cover' }} />
            </Box>
          ) : (
            <Box sx={{ width:48,height:36,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,color:'text.disabled',border:'1px dashed #ccc',borderRadius:1 }}>No Img</Box>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display:'flex', alignItems:'center', pl: level*2 }}>
            {level>0 && <Box sx={{ width:20, height:1, bgcolor:'divider', mr:1 }} />}
            {cat.name}
            {level>0 && <Chip label={`Level ${level}`} size="small" sx={{ ml:1, height:20 }} variant="outlined" />}
          </Box>
        </TableCell>
        <TableCell>{cat.description||'-'}</TableCell>
        <TableCell><Chip label={`${cat.productCount||0} products`} size="small" variant="outlined" /></TableCell>
        <TableCell><Chip label={cat.isActive? 'Active':'Inactive'} color={cat.isActive? 'success':'default'} size="small" /></TableCell>
        <TableCell>
          <Tooltip title="Edit"><IconButton size="small" onClick={()=>handleEditCategory(cat)}><EditIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" onClick={()=>handleDeleteCategory(cat.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        </TableCell>
      </TableRow>
      {cat.children?.length && renderCategoryTree(cat.children, level+1)}
    </React.Fragment>
  ));

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ name:cat.name, description:cat.description||'', parentId:cat.parentId||'', isActive: cat.isActive!==undefined? cat.isActive:true, imageUrl: cat.imageUrl||'' });
    setOpenDialog(true);
  };

  const handleDeleteCategory = async (id) => {
    if(!window.confirm('Delete this category?')) return;
    try { await adminService.deleteCategory(id); toast.success('Category deleted'); loadCategories(); } catch(e){ toast.error('Delete failed'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if(!categoryForm.name.trim()) { toast.error('Name required'); return; }
    try {
      setSaving(true);
      if(editingCategory) await adminService.updateCategory(editingCategory.id, categoryForm); else await adminService.createCategory(categoryForm);
      toast.success(editingCategory? 'Category updated':'Category created');
      setOpenDialog(false); setEditingCategory(null); setCategoryForm({ name:'', description:'', parentId:'', isActive:true, imageUrl:'' });
      loadCategories();
    } catch(e){ toast.error('Save failed'); } finally { setSaving(false); }
  };

  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:3 }}>
        <Typography variant="h5" fontWeight={700}>Categories</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={()=>{ setEditingCategory(null); setCategoryForm({ name:'', description:'', parentId:'', isActive:true, imageUrl:'' }); setOpenDialog(true); }}>Add Category</Button>
      </Box>
      {loading ? (
        <Box sx={{ display:'flex', justifyContent:'center', py:6 }}><CircularProgress /></Box>
      ) : categoriesTree.length === 0 ? (
        <Paper sx={{ p:4, textAlign:'center' }}>
          <Typography color="text.secondary">No categories yet. Create your first one.</Typography>
        </Paper>
      ) : (
        <Table component={Paper} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Products</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
            <TableBody>
              {renderCategoryTree(categoriesTree)}
            </TableBody>
        </Table>
      )}

      <Dialog open={openDialog} onClose={()=>setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory? 'Edit Category':'Add New Category'}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt:1 }}>
            <TextField fullWidth label="Name" value={categoryForm.name} onChange={e=>setCategoryForm(f=>({...f,name:e.target.value}))} required sx={{ mb:2 }} />
            <TextField fullWidth label="Description" multiline rows={3} value={categoryForm.description} onChange={e=>setCategoryForm(f=>({...f,description:e.target.value}))} sx={{ mb:2 }} />
            <Box sx={{ mb:2 }}>
              <Typography variant="subtitle2" gutterBottom>Category Image</Typography>
              {categoryForm.imageUrl && <Box mb={1} sx={{ width:220, aspectRatio:'4/3', border:'1px solid #ddd', borderRadius:1, overflow:'hidden' }}><img alt="Category" src={categoryForm.imageUrl} style={{ width:'100%',height:'100%', objectFit:'cover' }} /></Box>}
              <Button variant="outlined" component="label" startIcon={<ImageIcon />}> {categoryForm.imageUrl? 'Change Image':'Upload Image'}
                <input type="file" accept="image/*" hidden onChange={async (e)=>{ const file=e.target.files?.[0]; if(!file) return; try { const { default: up } = await import('../../services/uploadService'); const url = await up.uploadSingleProductImage(file, `category-${categoryForm.name||'unnamed'}`, 0, ()=>{}); setCategoryForm(f=>({...f,imageUrl:url})); toast.success('Image uploaded'); } catch(err){ toast.error('Upload failed'); } }} />
              </Button>
              {categoryForm.imageUrl && <Button size="small" sx={{ ml:2 }} color="error" onClick={()=>setCategoryForm(f=>({...f,imageUrl:''}))}>Remove</Button>}
            </Box>
            <FormControl fullWidth sx={{ mb:2 }}>
              <InputLabel>Parent Category (Optional)</InputLabel>
              <Select value={categoryForm.parentId} label="Parent Category (Optional)" onChange={e=>setCategoryForm(f=>({...f,parentId:e.target.value}))}>
                <MenuItem value=""><em>None (Root)</em></MenuItem>
                {flattenCategoriesForSelection(categoriesTree, editingCategory?.id).map(c=> <MenuItem key={c.id} value={c.id}>{c.displayName}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel control={<Switch checked={categoryForm.isActive} onChange={e=>setCategoryForm(f=>({...f,isActive:e.target.checked}))} />} label="Active" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpenDialog(false)}>Cancel</Button>
          <Button disabled={saving} onClick={handleSubmit} variant="contained">{editingCategory? 'Update':'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCategories;
